import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { DatePicker, Space, Spin, Form, Input, Checkbox, Button, App } from 'antd';
import { FormInstance } from 'antd/lib/form';

import { AppDispatch, RootState } from '../../../store';

import { isEmpty, isString } from 'lodash';

import dayjs, { isDayjs, Dayjs } from 'dayjs';

import axios from '../../../shared/axios';

import withErrorHandler from '../../../hoc/with-error-handler';

import * as actions from '../store/actions';

import Filter from '../../../shared/components/filter';
import { filterTypes } from '../../../shared/constants';
import { validateGuid, getTinyMCEEditorTheme } from '../../../shared/utils';

import {
  SystemMessage
} from '../../../shared/models';

import './styles.less';

export type SystemMessageEditComponentProps = {
  systemMessageId?: string | undefined;
  redirectUrl?: string;
}

const mceEditorId = 'systemMessageMceEditorId';

const EditItem = ({ systemMessageId, redirectUrl }: SystemMessageEditComponentProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const { notification, modal } = App.useApp();

  const formRef = useRef<FormInstance>(null);

  const { systemMessageTypes,
    systemMessageTypesLoading,
    databases,
    databasesLoading,
    systemMessageData,
  } = useSelector((state: RootState) => state.systemMessageEdit);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isNew, setIsNew] = useState<boolean>(true);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [editorLoading, setEditorLoading] = useState<boolean>(true);

  useEffect(() => {
    let promiseArray: Promise<any>[] = [dispatch(actions.getSystemMessageTypes()), dispatch(actions.getDatabases())];

    if (validateGuid(systemMessageId)) {
      promiseArray.push(dispatch(actions.getSystemMessageData({ systemMessageId })));

      setIsNew(false);
    }

    Promise.all(promiseArray).then(() => {
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    if (systemMessageData && formRef?.current) {
      if (isString(systemMessageData.databaseIds) && !isEmpty(systemMessageData.databaseIds)) {
        systemMessageData.databaseIdsArray = systemMessageData.databaseIds.split(',');
      }

      if (isString(systemMessageData.reminderDate) && !isEmpty(systemMessageData.reminderDate)) {
        systemMessageData.reminderDate = dayjs(systemMessageData.reminderDate);
      }

      formRef.current.resetFields([
        'id',
        'title',
        'isShownInMainDashboard',
        'isShownWhenLogin',
        'showOnDashboardLight',
        'type',
        'showPopup',
        'databaseIdsArray',
        'reminderDate',
      ]);

      setShowPopup(systemMessageData.showPopup === true);
    }
  }, [systemMessageData]);

  useEffect(() => {
    const editor = (window as any).tinymce.get(mceEditorId);

    setEditorLoading(true);

    console.log('MCE control init start');

    setTimeout(() => {
      (window as any).tinymce.init({
        selector: `div#${mceEditorId}`,
        auto_focus: mceEditorId,
        browser_spellcheck: true,
        max_height: 500,
        min_height: 350,
        ...getTinyMCEEditorTheme(),
        init_instance_callback: (editor: any) => {
          console.log('MCE control initialized');

          setEditorLoading(false);

          editor.on('SetContent', (e: any) => {
            handleBodyChange(editor.getContent({ format: 'text' }));
          });

          editor.on('input', (e: any) => {
            handleBodyChange(editor.getContent({ format: 'text' }));
          });
        },
        document_base_url: window.location.origin,
        relative_urls: false,
        remove_script_host: false,
        menubar: false,
        statusbar: false,
        plugins: 'lists advlist code preview link image media paste',
        toolbar_mode: 'sliding',
        image_title: true,
        automatic_uploads: true,
        file_picker_types: 'image media',
        file_picker_callback: function (callback: any, value: any, meta: any) {
          if (meta.filetype === 'image') {
            const input = document.createElement('input');
            input.setAttribute('type', 'file');
            input.setAttribute('accept', 'image/*');

            input.onchange = function () {
              const file = (this as any).files[0];

              if (file.size > 20 * 1024 * 1024) {
                notification.error({
                  message: 'Error',
                  top: 55,
                  duration: 10,
                  description: `File size exceeds 20 MB. Please select a smaller file.`,
                } as any);

                return;
              }

              const reader = new FileReader();

              reader.onload = function () {
                const base64 = reader?.result?.toString()?.split(',')[1];

                (window as any).tinymce.activeEditor.setProgressState(true);

                dispatch(actions.saveSystemMessageImage({ imageData: base64 }))
                  .then((imageUrl: any) => {
                    callback(imageUrl, { alt: file.name, title: file.name });
                  })
                  .finally(() => {
                    (window as any).tinymce.activeEditor.setProgressState(false);
                  });
              };

              reader.readAsDataURL(file);
            };

            input.click();
          } else if (meta.filetype === 'media') {
            const input = document.createElement('input');
            input.setAttribute('type', 'file');
            input.setAttribute('accept', 'video/*');

            input.onchange = function () {
              const file = (this as any).files[0];

              if (file.size > 20 * 1024 * 1024) {
                notification.error({
                  message: 'Error',
                  top: 55,
                  duration: 10,
                  description: `File size exceeds 20 MB. Please select a smaller file.`,
                } as any);
                return;
              }

              const reader = new FileReader();

              reader.onload = function () {
                const base64 = reader?.result?.toString()?.split(',')[1];

                (window as any).tinymce.activeEditor.setProgressState(true);

                dispatch(actions.saveSystemMessageImage({ imageData: base64 }))
                  .then((videoUrl: any) => {
                    callback(videoUrl, { title: file.name });
                  })
                  .finally(() => {
                    (window as any).tinymce.activeEditor.setProgressState(false);
                  });
              };

              reader.readAsDataURL(file);
            };

            input.click();
          }
        },
        media_live_embeds: true,
        toolbar:
          // eslint-disable-next-line
          'undo redo |\
          fontselect fontsizeselect |\
          bold italic underline strikethrough |\
          forecolor backcolor |\
          alignleft aligncenter alignright |\
          outdent indent |\
          bullist numlist |\
          code preview |\
          link |\
          image media',
      });
    }, 100);

    return () => {
      try {
        editor?.remove();
      } catch (e) {
        console.log(e);
      }
    };
  }, []);

  useEffect(() => {
    if (systemMessageData && !editorLoading) {
      (window as any).tinymce.activeEditor.getBody().innerHTML = systemMessageData.body;
      formRef.current?.setFieldsValue({ body: systemMessageData.body });
    }
  }, [systemMessageData, editorLoading]);

  const handleBodyChange = (newValueWithoutHTML: string): void => {
    formRef.current?.setFieldsValue({ body: newValueWithoutHTML });
  };

  const onCancelClick = (): void => {
    setIsLoading(true);
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  };

  const onUpdate = (): void => {
    let editor = (window as any).tinymce.get(mceEditorId);
    let textBody = editor.save();

    formRef.current?.validateFields().then((formData: SystemMessage) => {
      if (!isString(textBody) || isEmpty(textBody)) {
        return;
      }

      formData.showPopup = formData.showPopup ?? false;
      formData.showOnDashboardLight = formData.showOnDashboardLight ?? false;
      formData.isShownWhenLogin = formData.isShownWhenLogin ?? false;
      formData.isShownInMainDashboard = formData.isShownInMainDashboard ?? false;

      formData.body = textBody;
      formData.id = systemMessageId?.toString() ?? '';

      setIsLoading(true);

      if (formData.showPopup === true) {
        if (isString(formData.reminderDate) && !isEmpty(formData.reminderDate)) {
          formData.reminderDate = dayjs(formData.reminderDate).format('YYYY-MM-DD HH:mm');
        } else if (!isEmpty(formData.reminderDate) && isDayjs(formData.reminderDate)) {
          formData.reminderDate = (formData.reminderDate as Dayjs).format('YYYY-MM-DD HH:mm');
        }
      } else {
        formData.reminderDate = undefined;
      }

      formData.databaseIds = formData?.databaseIdsArray?.join(',');

      dispatch(actions.saveSystemMessage(formData)).then((message: any) => {
        modal.success({
          width: '500px',
          title: 'Success',
          content: <div dangerouslySetInnerHTML={{ __html: message.replace(/(?:\r\n|\r|\n)/g, '<br />') }} />,
          onOk: () => {
            if (isString(textBody) && !isEmpty(redirectUrl)) {
              window.location.href = redirectUrl ?? '';
            }
          },
        });
      });
    });
  };

  const onShowPopupChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setShowPopup(e.target.checked);
  };

  const onChangePopupDateTime = (value: Dayjs | null): void => {
    const coeff = 1000 * 60 * 5;
    if (value && isDayjs(value)) {
      const rounded = dayjs(Math.round(value.valueOf() / coeff) * coeff);
      formRef.current?.setFieldsValue({ reminderDate: rounded });
    }
  };

  return (
    <div className="system-messages-edit">
      <Spin spinning={isLoading}>
        <Form
          className="system-messages-edit-item"
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 14 }}
          layout="horizontal"
          disabled={isLoading}
          ref={formRef}
          initialValues={systemMessageData as any}
        >
          <Form.Item
            label="Title"
            name="title"
            rules={[
              {
                required: true,
                message: 'This field is required.',
                whitespace: true,
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Body"
            name="body"
            rules={[
              {
                required: true,
                message: 'This field is required.',
                whitespace: true,
              },
            ]}
          >
            <Spin spinning={editorLoading}>
              <div id={mceEditorId}></div>
            </Spin>
          </Form.Item>

          <Form.Item label="Show message in dashboard" name="isShownInMainDashboard" valuePropName="checked">
            <Checkbox></Checkbox>
          </Form.Item>

          <Form.Item label="Show message in Login page" name="isShownWhenLogin" valuePropName="checked">
            <Checkbox></Checkbox>
          </Form.Item>

          <Form.Item label="Show message in Dashboard Light" name="showOnDashboardLight" valuePropName="checked">
            <Checkbox></Checkbox>
          </Form.Item>

          <Filter
            filterType={filterTypes.dropdownWithLocalData}
            name={'type'}
            label={'Type'}
            data={systemMessageTypes as any}
            dataLoading={systemMessageTypesLoading}
            maxTagCount={1}
            mode={'single'}
            rules={[
              {
                required: true,
                message: 'This field is required.',
              },
            ]}
          />

          <Filter
            filterType={filterTypes.dropdownWithLocalData}
            name={'databaseIdsArray'}
            label={'Databases'}
            data={databases as any}
            dataLoading={databasesLoading}
            maxTagCount={1}
            placeholder={'All active databases'}
            tooltip={{
              title: 'Select databases (servers) or leave empty (to select all servers) you want to notify users in.',
            }}
          />

          <Form.Item
            label="Show popup"
            name="showPopup"
            valuePropName="checked"
            tooltip={{
              title: 'Set this to true if you want to notify users with popup when this message will be posted.',
            }}
          >
            <Checkbox value={showPopup} onChange={onShowPopupChange as any}></Checkbox>
          </Form.Item>

          <Form.Item
            label="Show Popup At"
            name="reminderDate"
            tooltip={{
              title: 'NOTICE! This feature uses server time. Configure popup to show up at certain time (optional).',
            }}
          >
            <DatePicker
              format="MM-DD-YYYY hh:mm A"
              showTime={{ format: 'HH:mm', minuteStep: 5 }}
              onChange={(value, dateString) => onChangePopupDateTime(value)}
              disabledDate={(current) => {
                return current && current.valueOf() < Date.now();
              }}
              disabled={showPopup === false}
            />
          </Form.Item>

          <div className="action-buttons">
            <Space>
              <Button onClick={onCancelClick}>Cancel</Button>
              <Button type="primary" onClick={onUpdate}>
                {isNew ? 'Save' : 'Update'}
              </Button>
            </Space>
          </div>
        </Form>
      </Spin>
    </div>
  );
};

export default withErrorHandler(EditItem, axios);